import "./App.css";

import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Select,
  Stack,
  Text,
} from "@indicina1/components-library";
import React, { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import validator from "validator";

import ConfirmSignup from "./component/confirmSignup";
import CheckIcon from "./component/icon/CheckIcon";
import DotIcon from "./component/icon/DotIcon";
import config from "./config/config";
import Decide from "./services/decide";
import { DecideFormInput, DecideServiceInterface } from "./types/decide";
import countries from "./utils/countries";

declare global {
  interface Window {
    decideUrl: string;
    lintrk: (action: string, options: { [key: string]: number }) => void;
  }
}

function App() {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<DecideFormInput>({
    mode: "onBlur",
    defaultValues: { countryCode: "+234" },
  });
  const [formError, setFormError] = useState<string | undefined>();
  const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
  const [domainSuggestion, setDomainSuggestion] = useState<string>("");
  const [slug, setSlug] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [showDomain, setShowDomain] = useState<boolean>(false);
  const [clickVerify, setClickVerify] = useState<boolean>(false);

  const notificationRef = useRef<null | HTMLFormElement>(null);
  const decideService: DecideServiceInterface = new Decide(
    window?.decideUrl || config.base_url
  );

  const validateSlug = async (str: string): Promise<boolean> => {
    if (!validator.isAlphanumeric(str)) {
      return false;
    }
    return decideService.slugValidator(str);
  };

  const getSlugSuggestions = async (businessName: string): Promise<string> => {
    const suggestion = await decideService.getDomainSuggestion(businessName);
    setDomainSuggestion(suggestion);
    return suggestion;
  };

  const validatePassword = (
    value: string,
    options: validator.strongPasswordOptions
  ) => {
    return validator.isStrongPassword(value, {
      minLength: 0,
      minLowercase: 0,
      minUppercase: 0,
      minNumbers: 0,
      minSymbols: 0,
      ...options,
    });
  };

  const onSubmit: SubmitHandler<DecideFormInput> = async (data) => {
    setFormError(undefined);

    const payload = {
      ...data,
      slug: data?.slug || domainSuggestion,
    };

    const res = await decideService.register(payload);

    notificationRef?.current?.scrollIntoView();

    if (res?.status === "error") {
      setFormError(res?.errorMessage);
      return;
    }

    if (res?.status === "success") {
      setSlug(res?.data?.verification?.slug);
      setVerificationId(res?.data?.verification?.id);
      setSignupSuccess(true);
    }
  };

  const passwordValue = watch("password", "");
  const phoneValue = watch("phoneNumber", "");
  useEffect(() => {
    if ("lintrk" in window) {
      window?.lintrk("track", { conversion_id: config.linkedin_tracking_id });
    }
  }, []);

  const handleShowCustomDomain = () => {
    setShowDomain(true);
  };

  useEffect(() => {
    if (slug) {
      localStorage.setItem("slug", slug);
    }
    const data = localStorage.getItem("slug");
    setSlug(data);
    if (clickVerify) {
      localStorage.clear();
    }
  }, [slug, clickVerify]);

  useEffect(() => {
    if (verificationId) {
      localStorage.setItem("verificationId", verificationId);
    }
    const data = localStorage.getItem("verificationId");
    setVerificationId(data);
    if (clickVerify) {
      localStorage.clear();
    }
  }, [verificationId, clickVerify]);

  const userEmail = watch("email");
  const usersBusinessName = watch('businessName')

  const [customDomainError, setcustomDomainError] = useState(false)

  useEffect(() => {
    const validateCustomDomain = async () => {
      const suggest = await getSlugSuggestions(usersBusinessName);
      const res = await validateSlug(suggest);

      if (res === false) {
        setcustomDomainError(true);
      }
      else {
        setcustomDomainError(false)
      }
    };

    validateCustomDomain();
  }, [usersBusinessName]);


  return (
    <>
      {signupSuccess || (slug && verificationId) ? (
        <ConfirmSignup verificationId={verificationId} slug={slug} userEmail={userEmail} setClickVerify={setClickVerify}/>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          ref={notificationRef}
          data-testid="decide-signup-form"
        >
          <Stack p={6} spacing="24px">
            <Stack spacing="33px">
              <Stack direction="column">
                <Heading level="h4" textAlign="left">
                  Get Started
                </Heading>
                <Text alignment="left" size="large">
                  Create your free Decide account
                </Text>
              </Stack>
              <Stack>
                {formError && (
                  <Alert
                    description={formError}
                    hasIcon={true}
                    status="error"
                    isDismissible
                  />
                )}
              </Stack>
              <Stack spacing="40px">
                <Stack spacing="20px">
                  <Stack direction="row" spacing="20px">
                    <Input
                      label="First Name"
                      placeholder="Enter your First name"
                      data-testid="decide-signup-firstname"
                      errorMessage={errors?.firstName?.message}
                      {...register("firstName", {
                        required: {
                          value: true,
                          message: "First Name is required",
                        },
                        minLength: {
                          value: 2,
                          message:
                            "First Name cannot be less than 2 characters",
                        },
                        maxLength: {
                          value: 40,
                          message:
                            "First Name cannot be more than 40 characters",
                        },
                        validate: (value) => validator.isAlphanumeric(value),
                      })}
                    />
                    <Input
                      label="Last Name"
                      placeholder="Enter your Last name"
                      data-testid="decide-signup-lastname"
                      errorMessage={errors?.lastName?.message}
                      {...register("lastName", {
                        required: {
                          value: true,
                          message: "Last Name is required",
                        },
                        minLength: {
                          value: 2,
                          message: "Last Name cannot be less than 2 characters",
                        },
                        maxLength: {
                          value: 40,
                          message:
                            "Last Name cannot be more than 40 characters",
                        },
                        validate: (value) =>
                          validator.isAlphanumeric(value) ||
                          "Last Name is not valid",
                      })}
                    />
                  </Stack>
                  <Input
                    label="Work Email"
                    placeholder="you@email.com"
                    errorMessage={errors?.email?.message}
                    data-testid="decide-signup-email"
                    {...register("email", {
                      required: "Email is required",
                      minLength: 5,
                      maxLength: 40,
                      validate: (value) =>
                        validator.isEmail(value) || "Invalid email address",
                    })}
                  />
                  <Flex alignItems="flex-end">
                    <Stack w="xs">
                      <Text as="label">Phone Number </Text>
                      <Select
                        id="countryCode"
                        data={countries}
                        variant="filled"
                        data-testid="decide-signup-country-code"
                        defaultValue="+234"
                        size="regular"
                        __css={{
                          paddingTop: "1px",
                          fontWeight: "normal",
                        }}
                        {...register("countryCode", {
                          required: {
                            value:
                              phoneValue && getValues("countryCode") === ""
                                ? true
                                : false,
                            message: "Country code is required",
                          },
                        })}
                      />
                    </Stack>

                    <Input
                      type="number"
                      placeholder="8021234567"
                      data-testid="decide-signup-phone"
                      {...register("phoneNumber", {
                        required: {
                          value: phoneValue === "" ? true : false,
                          message: "Phone number is required",
                        },
                        minLength: {
                          value: 5,
                          message: "Phone number is less than 5 digits",
                        },
                        maxLength: {
                          value: 20,
                          message: "Phone number is longer than 20 digit",
                        },
                        validate: (value) => {
                          if (typeof value !== "undefined" && value !== "") {
                            return (
                              validator.isMobilePhone(value) ||
                              "Invalid phone number"
                            );
                          }
                        },
                      })}
                    />
                  </Flex>
                  {(errors?.phoneNumber?.message ||
                    errors?.countryCode?.message) && (
                      <Text
                        css={{
                          color: "var(--indicina-colors-red-500)",
                          fontSize: "14px",
                          marginTop: "0.25rem !important",
                        }}
                      >
                        {errors?.countryCode?.message ||
                          errors?.phoneNumber?.message}
                      </Text>
                    )}

                  <Input
                    label="Business Name"
                    placeholder="Business Name"
                    data-testid="decide-signup-business-name"
                    {...register("businessName", {
                      onBlur: (e) => {
                        if (e.target.value && !errors?.businessName?.message) {
                          getSlugSuggestions(watch("businessName"));
                        }
                      },
                      required: {
                        value: true,
                        message: "Business Name is required",
                      },
                      minLength: {
                        value: 2,
                        message:
                          "Business Name cannot be less than 2 characters",
                      },
                      maxLength: {
                        value: 40,
                        message:
                          "Business Name should not exceed 40 characters",
                      },
                    })}
                  />
                  <Flex data-testid="decide-signup-slug">
                    {showDomain ? (
                      <Input
                        label="Custom Domain"
                        placeholder="Enter your Short Name"
                        data-testid="decide-signup-custom-slug"
                        rightAddon=".decide.indicina.co"
                        leftAddon="https://"
                        errorMessage={errors?.slug?.message}
                        defaultValue={domainSuggestion}
                        {...register("slug", {
                          required: {
                            value: true,
                            message: "Custom Domain is required",
                          },
                          minLength: {
                            value: 4,
                            message:
                              "Custom Domain cannot be less than 4 characters",
                          },
                          maxLength: {
                            value: 12,
                            message:
                              "Custom Domain cannot be more than 12 characters",
                          },
                          validate: async (value) => {
                            const res = await validateSlug(value);
                            const suggestion = await getSlugSuggestions(
                              getValues("businessName")
                            );
                            if (res === false && value !== suggestion) {
                              return "Custom Domain is already in use. Please try another";
                            }
                          },
                        })}
                      />
                    ) : domainSuggestion && !showDomain ? (
                      <Flex flexDirection="column" >
                        <Flex alignItems="center">
                          Suggested domains:
                          <Text
                            ml="5px"
                            color="#0335DC"
                            fontWeight="500"
                          >{`https://${domainSuggestion}.decide.indicina.co`}</Text>
                          <Button
                            variant="tertiary"
                            ml="10px"
                            onClick={handleShowCustomDomain}
                            role="edit-slug-btn"
                          >
                            Edit Domain
                          </Button>
                        </Flex>
                        {customDomainError && (
                          <Box as="span" fontSize="sm" color="red.500" > {customDomainError && `custom domain ${domainSuggestion} is already in use, click the edit button above to edit`}
                          </Box>
                        )}
                      </Flex>
                    ) : (
                      ""
                    )}
                  </Flex>
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Password"
                    errorMessage={errors?.password?.message}
                    data-testid="decide-signup-password"
                    {...register("password", {
                      required: {
                        value: true,
                        message: "Password is required",
                      },
                      validate: (value) =>
                        validator.isStrongPassword(value, {
                          minLength: 8,
                          minLowercase: 1,
                          minUppercase: 1,
                          minNumbers: 1,
                          minSymbols: 1,
                        }),
                    })}
                  />
                </Stack>

                <Stack color="gray.500">
                  <Text fontWeight="bold">Password must</Text>
                  <Flex dir="column">
                    {validatePassword(passwordValue, { minLength: 8 }) ? (
                      <CheckIcon />
                    ) : (
                      <DotIcon />
                    )}
                    <Text>be at least 8 characters long</Text>
                  </Flex>
                  <Flex dir="column">
                    {validatePassword(passwordValue, { minLowercase: 1 }) ? (
                      <CheckIcon />
                    ) : (
                      <DotIcon />
                    )}
                    <Text>contain a lowercase letter (a-z)</Text>
                  </Flex>
                  <Flex dir="column">
                    {validatePassword(passwordValue, { minUppercase: 1 }) ? (
                      <CheckIcon />
                    ) : (
                      <DotIcon />
                    )}
                    <Text>contain a uppercase letter (A-Z)</Text>
                  </Flex>
                  <Flex>
                    {validatePassword(passwordValue, { minNumbers: 1 }) ? (
                      <CheckIcon />
                    ) : (
                      <DotIcon />
                    )}
                    <Text>contain a number (0-9)</Text>
                  </Flex>
                  <Flex>
                    {validatePassword(passwordValue, { minSymbols: 1 }) ? (
                      <CheckIcon />
                    ) : (
                      <DotIcon />
                    )}
                    <Text>contain a special character</Text>
                  </Flex>
                </Stack>

                <Button type="submit" isLoading={isSubmitting}>
                  Create Account
                </Button>
              </Stack>
            </Stack>
            <Text>
              By clicking Continue, you acknowledge that you have read and
              accepted the{" "}
              <a href="https://indicina.decide.indicina.co/docs/Decide_Terms_of_Service_29.11.21.pdf" target="_blank">
                <Text as="span" isInline textStyle="bold">
                  Terms of Service
                </Text>
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" target="_blank">
                <Text as="span" isInline textStyle="bold">
                  Privacy Policy
                </Text>
              </a>{" "}
              .
            </Text>
          </Stack>
        </form>
      )}
    </>
  );
}

export default App;
